(() => {
  const drawer = document.getElementById('CartDrawer');
  const overlay = document.querySelector('.cart-drawer-overlay[data-cart-drawer-close]');
  const openTriggers = document.querySelectorAll('[data-cart-drawer-open]');
  const closeTriggers = document.querySelectorAll('[data-cart-drawer-close]');
  const itemsEl = document.querySelector('[data-cart-drawer-items]');
  const footerEl = document.querySelector('[data-cart-drawer-footer]');
  const subtotalEl = document.querySelector('[data-cart-drawer-subtotal]');
  const countEl = document.querySelector('[data-cart-count]');
  const cartLink = document.querySelector('.site-cart');

  if (!drawer || !itemsEl) return;

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let lastTrigger = null;

  const getFocusable = () => Array.from(drawer.querySelectorAll(focusableSelector))
    .filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR'
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2).replace('.', ',') + ' €';
    }
  };

  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[character]));

  const setLoading = (isLoading) => {
    drawer.classList.toggle('is-loading', Boolean(isLoading));
  };

  const updateCount = (cart) => {
    if (!countEl || typeof cart.item_count === 'undefined') return;

    const count = Number(cart.item_count || 0);
    countEl.textContent = count;
    countEl.hidden = count < 1;
    countEl.setAttribute('aria-label', count === 1 ? '1 Artikel im Warenkorb' : `${count} Artikel im Warenkorb`);

    if (cartLink) {
      cartLink.setAttribute('aria-label', count > 0 ? `Warenkorb öffnen, ${count} Artikel im Warenkorb` : 'Warenkorb öffnen');
    }
  };

  const renderCart = (cart) => {
    updateCount(cart);

    if (!cart.items || cart.items.length === 0) {
      itemsEl.innerHTML = '<p class="cart-drawer__empty">Dein Warenkorb ist leer.</p>';
      if (footerEl) footerEl.hidden = true;
      if (subtotalEl) subtotalEl.textContent = formatMoney(0);
      return;
    }

    itemsEl.innerHTML = cart.items.map((item) => {
      const image = item.image
        ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
        : '<span class="cart-drawer-item__placeholder"></span>';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p>${escapeHtml(item.variant_title)}</p>`
        : '';
      return `
        <article class="cart-drawer-item" data-cart-item-key="${escapeHtml(item.key)}">
          <a class="cart-drawer-item__media" href="${escapeHtml(item.url)}" aria-label="${escapeHtml(item.product_title)} ansehen">
            ${image}
          </a>
          <div class="cart-drawer-item__content">
            <div class="cart-drawer-item__top">
              <div>
                <h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></h3>
                ${variant}
              </div>
              <strong>${formatMoney(item.final_line_price)}</strong>
            </div>
            <div class="cart-drawer-item__actions">
              <div class="cart-drawer-qty" aria-label="Menge ändern">
                <button type="button" data-cart-qty-minus aria-label="Menge reduzieren">−</button>
                <input type="number" min="0" inputmode="numeric" value="${item.quantity}" data-cart-qty-input aria-label="Menge">
                <button type="button" data-cart-qty-plus aria-label="Menge erhöhen">+</button>
              </div>
              <button type="button" class="cart-drawer-item__remove" data-cart-remove>Entfernen</button>
            </div>
          </div>
        </article>
      `;
    }).join('');

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    if (footerEl) footerEl.hidden = false;
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    if (!response.ok) throw new Error('Warenkorb konnte nicht geladen werden.');
    return response.json();
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const cart = await fetchCart();
      renderCart(cart);
      return cart;
    } finally {
      setLoading(false);
    }
  };

  const open = async (trigger = null) => {
    if (trigger) lastTrigger = trigger;
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-open');
    if (overlay) overlay.hidden = false;
    openTriggers.forEach((item) => item.setAttribute('aria-expanded', 'true'));

    try {
      await refresh();
    } catch (error) {
      console.error(error);
    }

    const focusTarget = drawer.querySelector('[data-cart-drawer-close]') || getFocusable()[0];
    if (focusTarget) setTimeout(() => focusTarget.focus(), 40);
  };

  const close = () => {
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    document.documentElement.classList.remove('cart-drawer-open');
    if (overlay) overlay.hidden = true;
    openTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (lastTrigger) lastTrigger.focus();
  };

  const changeItem = async (key, quantity) => {
    setLoading(true);
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity || 0)) })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Warenkorb konnte nicht aktualisiert werden.');
      }

      const cart = await response.json();
      renderCart(cart);
    } catch (error) {
      console.error(error);
      await refresh();
    } finally {
      setLoading(false);
    }
  };

  openTriggers.forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      open(trigger);
    });
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || link.matches('[data-cart-drawer-open]') || link.matches('[data-cart-page-allow]')) return;

    const url = new URL(link.href, window.location.origin);
    if (url.origin === window.location.origin && url.pathname === '/cart') {
      event.preventDefault();
      open(link);
    }
  });

  closeTriggers.forEach((trigger) => trigger.addEventListener('click', close));

  document.addEventListener('keydown', (event) => {
    if (drawer.getAttribute('aria-hidden') !== 'false') return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = getFocusable();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  itemsEl.addEventListener('click', (event) => {
    const item = event.target.closest('[data-cart-item-key]');
    if (!item) return;
    const key = item.dataset.cartItemKey;
    const input = item.querySelector('[data-cart-qty-input]');
    const current = Number(input?.value || 0);

    if (event.target.closest('[data-cart-qty-minus]')) changeItem(key, current - 1);
    if (event.target.closest('[data-cart-qty-plus]')) changeItem(key, current + 1);
    if (event.target.closest('[data-cart-remove]')) changeItem(key, 0);
  });

  itemsEl.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-qty-input]');
    if (!input) return;
    const item = input.closest('[data-cart-item-key]');
    if (!item) return;
    changeItem(item.dataset.cartItemKey, input.value);
  });

  window.SkateCartDrawer = {
    open,
    close,
    refresh,
    refreshAndOpen: open
  };
})();
